import React, { useEffect, useState } from "react";
import "./ToolbarList.scss";

import classNames from "classnames";

import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { SUPPORTED_SHOWCASE_SOCIAL_MEDIA } from "src/app/CreatorShowcase/CreatorShowcase";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { MaterialTooltip } from "src/app/components/MaterialTooltip/MaterialTooltip";
import { translateMessage } from "src/app/methods/translateMessage";
import { ReactComponent as ChevronDown } from "src/images/chevron-down.svg";
import { camelCaseToSeparatedWords } from "src/utils/methods";
import _ from "lodash";
import {
  setNewLayout,
  setReportGridElements,
  setReportHiddenGridElements,
  setReportLayout,
} from "../../../../redux";
import { SocialProvider } from "../../../../types";
import CustomCheckbox from "../../../components/CustomCheckbox/CustomCheckbox";
import { METRIC_FIELDS } from "../../utils/variables";
import { ToolbarListCreatorMetaFields } from "../ToolbarListCreatorMetafields/ToolbarListCreatorMetaFields";
import { ToolbarListItem } from "../ToolbarListItem/ToolbarListItem";
import { ToolbarListPublicationMetaFields } from "../ToolbarListPublicationMetaFields/ToolbarListPublicationMetaFields";
import { ToolbarListTableSummary } from "../ToolbarListTableSummary/ToolbarListTableSummary";
import { ToolbarListTableSummaryByProvider } from "../ToolbarListTableSummaryByProvider/ToolbarListTableSummaryByProvider";
import { ToolbarListPublicationSummaryByProvider } from "../ToolbarListPublicationSummaryByProvider/ToolbarListPublicationSummaryByProvider";
import { addElementsToLayout } from "../../functions/addElementsToLayout";
import { sortLayout } from "../../functions/sortLayout";

export const TIK_TOK_BLACKLIST = [
  "avg-views",
  "credibility",
  "location-by-city",
];
export const YOUTUBE_BLACKLIST = [
  "avg-views",
  "credibility",
  "location-by-city",
  "keywords",
  "creators-brand-affinity",
  "audience-interests",
  "audience-brand-affinity",
  "creator-interests",
];
export const SNAPCHAT_WHITELIST = [
  "avg-views",
  "bio",
  "followers",
  "recently-published",
  "audience-age",
  "female-per-age",
  "male-per-age",
  "geographical-reach",
];

export const FACEBOOK_WHITELIST = ["bio", "followers"];

function ToolbarList(props) {
  const {
    provider,
    format,
    open,
    toolbarListProps,
    disableExpanding,
    isReport,
    intl,
    dynamicProvider,
    noBottomBorder,
    hideCheckbox,
    campaignId,
  } = props;

  const { gridElements, hiddenGridElements } = toolbarListProps;

  const [showList, setShowList] = useState(open);
  const [sectionVisible, setSectionVisible] = useState(false);
  const [listElements, setListElements] = useState([]);

  const {
    reportReducer: { layout, campaigns },
  } = useSelector((state) => state);

  const { publicationsSummary } = campaigns[campaignId] || {};

  const dispatch = useDispatch();

  const socialProvider = provider
    .replace("table-summary-", "")
    .replace(`-${campaignId}`, "");

  const renderHeader = () => {
    if (dynamicProvider) return provider;

    const defaultKey = "toolbar_publication_type_not_defined";
    if (provider.includes("table-summary-")) {
      return translateMessage({
        intl,
        id: `ws_${socialProvider || defaultKey}`,
        defaultMessage: `${camelCaseToSeparatedWords(socialProvider || "notDefined")}`,
      });
    }

    if (provider.includes("publication-summary-")) {
      const socialProvider = provider.replace("publication-summary-", "");
      return translateMessage({
        intl,
        id: `ws_${socialProvider || defaultKey}`,
        defaultMessage: `${camelCaseToSeparatedWords(socialProvider || "notDefined")}`,
      });
    }

    switch (provider) {
      case "metadata":
        if (isReport) {
          return translateMessage({
            intl,
            id: "ws_campaign_details",
            defaultMessage: "Campaign details",
          });
        }
        return translateMessage({
          intl,
          id: "ws_all",
          defaultMessage: "All",
        });

      case SocialProvider.Instagram:
        return "Instagram";

      case SocialProvider.Youtube:
        return "Youtube";

      case SocialProvider.TikTok:
        return "TikTok";

      case SocialProvider.Snapchat:
        return "Snapchat";

      case SocialProvider.Twitter:
        return "X";
      case SocialProvider.Facebook:
        return "Facebook";

      case "creator-insights":
        return translateMessage({
          intl,
          id: "ws_creator_insights",
          defaultMessage: "Creator Insights",
        });

      case "audience-data":
        return translateMessage({
          intl,
          id: "ws_campaign_audience_data",
          defaultMessage: "Campaign Audience Data",
        });

      case "audience-overlap":
        return translateMessage({
          intl,
          id: "ws_audience_overlap",
          defaultMessage: "Audience Overlap",
        });

      case "publication":
        return translateMessage({
          intl,
          id: "ws_all_publications",
          defaultMessage: "All Publications",
        });

      case "table-summary":
        return translateMessage({
          intl,
          id: "ws_summary",
          defaultMessage: "Summary",
        });

      case "post-data":
        return translateMessage({
          intl,
          id: "ws_executive_summary",
          defaultMessage: "Executive Summary",
        });

      case "creator":
        return translateMessage({
          intl,
          id: "ws_creators_in_the_campaign",
          defaultMessage: "Creators in the Campaign",
        });

      case "comments-analysis":
        return translateMessage({
          intl,
          id: "ws_comments_analysis",
          defaultMessage: "Comments analysis",
        });
    }
  };

  const getSectionId = () => {
    if (provider === "table-summary") {
      return `#header-1-table-summary`;
    }

    if (provider.includes("table-summary")) {
      return `#header-2-${provider}`;
    }

    switch (provider) {
      case "metadata":
        return "#header-2-metadata";

      case "creator-insights":
        return "#header-2-creator-insights";

      case "audience-data":
        return "#header-2-audience-data";

      case "audience-overlap":
        return "#header-2-audience-overlap";

      case "publication":
        return "#header-2-publication";

      case "post-data":
        return "#header-2-post-data";

      case "creator":
        return "#header-2-creator";

      case "comments-analysis":
        return "#header-1-comments-analysis";
    }
  };

  const getMetricGridElements = () => {
    const newGridElements =
      METRIC_FIELDS.map((item) => {
        const element = gridElements.find(
          (el) => el.type === item.value && el.provider === provider,
        );
        if (element) {
          return {
            ...element,
            value: element.label,
            label: item.label,
            campaignId: element.campaignId,
          };
        }
        return undefined;
      }) || [];

    const newHiddenElements =
      METRIC_FIELDS.map((item) => {
        const element = hiddenGridElements.find(
          (el) => el.type === item.value && el.provider === provider,
        );
        if (element) {
          return {
            ...element,
            value: element.label,
            label: item.label,
            campaignId: element.campaignId,
          };
        }
        return undefined;
      }) || [];

    return _.compact([...newGridElements, ...newHiddenElements]);
  };

  const getGridElementsByType = (type, label) => {
    const newGridElements =
      gridElements
        ?.filter((item) => {
          if (dynamicProvider) {
            return item.section === type;
          }
          return item.gridBoxType === type;
        })
        ?.map((item) => {
          return {
            label: item[label],
            value: item.label,
            campaignId: item.campaignId,
          };
        }) || [];

    const newHiddenElements =
      hiddenGridElements
        ?.filter((item) => item.gridBoxType === type)
        ?.map((item) => {
          return {
            label: item[label],
            value: item.label,
            campaignId: item.campaignId,
          };
        }) || [];

    return [...newGridElements, ...newHiddenElements];
  };

  const getFilteredFields = () => {
    if (dynamicProvider) return getGridElementsByType(dynamicProvider, "title");

    if (provider === "table-summary") {
      return getGridElementsByType(provider, "label");
    }

    if (provider.includes("table-summary-")) {
      if (!publicationsSummary) return [];
      return (
        publicationsSummary[socialProvider]?.summary?.map((item) => ({
          ...item,
          campaignId,
        })) || []
      );
    }

    if (provider.includes("publication-summary")) {
      if (!publicationsSummary) return [];

      return (
        publicationsSummary[
          provider.replace("publication-summary-", "")
        ]?.summary?.map((item) => ({ ...item, campaignId })) || []
      );
    }

    switch (provider) {
      case "metadata":
        return getGridElementsByType("metadata", "name");

      case SocialProvider.Instagram:
      case SocialProvider.Twitter:
        return getMetricGridElements().filter(
          (item) => item.value !== "avg-views",
        );

      case SocialProvider.TikTok:
        return getMetricGridElements().filter(
          (item) => !TIK_TOK_BLACKLIST.includes(item.value),
        );

      case SocialProvider.Youtube:
        return getMetricGridElements().filter(
          (item) => !YOUTUBE_BLACKLIST.includes(item.value),
        );
      case SocialProvider.Snapchat:
        return getMetricGridElements().filter((item) =>
          SNAPCHAT_WHITELIST.includes(item.type),
        );
      case SocialProvider.Facebook:
        return getMetricGridElements().filter((item) =>
          FACEBOOK_WHITELIST.includes(item.type),
        );
      case "creator-insights":
        return getGridElementsByType("creator-insights", "name");

      case "audience-data":
        return getGridElementsByType("audience-data", "name");

      case "audience-overlap":
        const platforms = getGridElementsByType("overlap-platform", "name");
        const charts = getGridElementsByType("overlap-chart", "name");
        const creators = getGridElementsByType("overlap-creator", "name").sort(
          (a, b) => a.value.localeCompare(b.value),
        );
        return [...platforms, ...charts, ...creators];

      case "post-data":
        return getGridElementsByType("post-data", "name");

      case "creator":
        return getGridElementsByType("creator", "title");

      case "publication":
        return getGridElementsByType("publication", "title");

      case "comments-analysis":
        const sentiments = getGridElementsByType("sentiment-analysis", "name");
        const comments = getGridElementsByType("comments", "name");
        return [...sentiments, ...comments];

      default:
        return [];
    }
  };

  const toggleSectionVisibility = () => {
    if (sectionVisible) {
      hideSection();
    } else {
      showSection();
    }
  };

  const getSectionElementsInLayout = () => {
    return gridElements.filter((item) => {
      if (dynamicProvider) {
        return (
          item.section === dynamicProvider ||
          compareWithHeaders({
            value: item.label,
            provider: dynamicProvider,
            equal: true,
          })
        );
      }

      return (
        item.gridBoxType === provider ||
        item.section === provider ||
        compareWithHeaders({
          value: item.label,
          provider,
          equal: true,
        })
      );
    });
  };

  const showSection = async () => {
    const newHiddenGridElements = hiddenGridElements.filter((item) => {
      if (dynamicProvider) {
        return (
          item.section !== dynamicProvider &&
          item.label !== `header-${dynamicProvider}` &&
          item.label !== `header-2-${dynamicProvider}`
        );
      }

      return (
        item.gridBoxType !== provider &&
        item.section !== provider &&
        item.label !== `header-${provider}` &&
        item.label !== `header-2-${provider}`
      );
    });

    const gridElementsToAdd = hiddenGridElements.filter((item) => {
      if (dynamicProvider) {
        return (
          item.section === dynamicProvider ||
          item.label === `header-${dynamicProvider}` ||
          item.label === `header-2-${dynamicProvider}`
        );
      }

      return (
        item.gridBoxType === provider ||
        item.section === provider ||
        item.label === `header-${provider}` ||
        item.label === `header-2-${provider}`
      );
    });

    dispatch(setReportGridElements([...gridElements, ...gridElementsToAdd]));
    dispatch(setReportHiddenGridElements(newHiddenGridElements));

    const newLayout = [...layout];

    const setLayout = isReport ? setReportLayout : setNewLayout;

    const publicationsVisible = newLayout.find((item) =>
      item.i.includes("publication"),
    );

    if (dynamicProvider && publicationsVisible) {
      await addDynamicSectionToLayout({
        gridElementsToAdd,
        newLayout,
        setLayout,
      });
    } else {
      await injectSection({ gridElementsToAdd, newLayout, setLayout });
    }
  };

  const injectSection = async (props) => {
    const { gridElementsToAdd, newLayout, setLayout } = props;

    const sortedLayout = sortLayout(newLayout);
    const rowsPerPage = format.rows;

    const sectionElements = getSectionElementsInLayout();

    let injectionY = 0;

    if (sectionElements.length > 0) {
      const lastSectionElement = sectionElements[sectionElements.length - 1];

      const lastLayoutElement = newLayout.find(
        (item) => item.i === lastSectionElement.label,
      );

      injectionY = lastLayoutElement.y + lastLayoutElement.h;
    } else {
      const scroller = document.querySelector(".template-generator");
      const scrollTop = scroller.scrollTop;
      const scrollYInGrid = scrollTop / format.rowHeight;

      const currentPage = Math.floor(scrollYInGrid / rowsPerPage);

      injectionY = (currentPage + 1) * rowsPerPage;
    }

    const firstPart = sortedLayout.filter((el) => el.y < injectionY);
    const lastPart = sortedLayout.filter((el) => el.y >= injectionY);

    const layoutWithInjected = await addElementsToLayout(
      gridElementsToAdd,
      [...firstPart],
      format,
      hiddenGridElements,
      true,
    );

    const lastInjected = layoutWithInjected[layoutWithInjected.length - 1];
    const injectedBottomY = lastInjected.y + lastInjected.h;

    let shiftedLastPart = [];

    if (lastPart.length > 0) {
      const firstOldY = lastPart[0].y;

      const originalPageStart =
        Math.floor(firstOldY / rowsPerPage) * rowsPerPage;

      const originalPageEnd = originalPageStart + rowsPerPage;

      const nextPageStart =
        Math.ceil(injectedBottomY / rowsPerPage) * rowsPerPage;

      const pagesTaken = Math.ceil(
        (injectedBottomY - injectionY) / rowsPerPage,
      );

      const firstPageShift = nextPageStart - firstOldY;

      const fullPagesShift = pagesTaken * rowsPerPage;

      shiftedLastPart = lastPart.map((item) => {
        const isFirstPage =
          item.y >= originalPageStart && item.y < originalPageEnd;

        return {
          ...item,
          y: item.y + (isFirstPage ? firstPageShift : fullPagesShift),
        };
      });
    }

    dispatch(setLayout([...layoutWithInjected, ...shiftedLastPart]));

    const sectionId = getSectionId();
    const element = document.querySelector(sectionId);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const addDynamicSectionToLayout = (props) => {
    const { gridElementsToAdd, newLayout, setLayout } = props;

    const filteredLayout = newLayout.filter(
      (item) =>
        item.i.slice(0, 11) === "publication" ||
        item.i.slice(0, 15) === "top-publication" ||
        item.i.slice(0, 24) === "header-2-top-publication",
    );
    const { rows } = format;

    const lastElementEndY =
      Math.max(...filteredLayout.map((item) => item.y)) + 7;
    const borderRow = lastElementEndY + (rows - (lastElementEndY % rows));

    const mappedLayout = newLayout.map((item) => {
      if (item.i.includes("separator")) return item;

      if (item.y >= borderRow) {
        return {
          ...item,
          y: item.y + rows,
        };
      }

      return item;
    });

    injectSection({ gridElementsToAdd, newLayout: mappedLayout, setLayout });

    return mappedLayout;
  };

  const compareWithHeaders = ({ value, provider, equal }) => {
    if (equal) {
      return (
        value === `header-1-${provider}` ||
        value === `header-2-${provider}` ||
        value === `header-3-${provider}`
      );
    }

    return (
      value !== `header-1-${provider}` &&
      value !== `header-2-${provider}` &&
      value !== `header-3-${provider}`
    );
  };

  const hideSection = () => {
    const newGridElements = gridElements.filter((item) => {
      if (dynamicProvider) {
        return (
          item.section !== dynamicProvider &&
          compareWithHeaders({ value: item.label, provider: dynamicProvider })
        );
      }

      return (
        item.gridBoxType !== provider &&
        item.section !== provider &&
        compareWithHeaders({ value: item.label, provider })
      );
    });

    const hiddenGridElementsToAdd = getSectionElementsInLayout();

    dispatch(setReportGridElements(newGridElements));
    dispatch(
      setReportHiddenGridElements([
        ...hiddenGridElements,
        ...hiddenGridElementsToAdd,
      ]),
    );
  };

  useEffect(() => {
    let newListElements = getFilteredFields();

    if (campaignId) {
      newListElements = newListElements?.filter(
        (item) => item.campaignId === campaignId,
      );
    }

    setListElements(newListElements);
  }, [gridElements, hiddenGridElements, publicationsSummary]);

  useEffect(() => {
    if (provider.includes("table-summary-")) {
      const elementFound = gridElements.find(
        (item) => item.section === provider,
      );

      if (elementFound) {
        setSectionVisible(true);
      } else {
        setSectionVisible(false);
      }
      return;
    }

    if (!listElements?.length) return;

    if (
      listElements.every((item) =>
        gridElements?.find((el) => {
          let label = item.value;
          if (
            SUPPORTED_SHOWCASE_SOCIAL_MEDIA.includes(provider) &&
            !label.includes(provider)
          ) {
            label = `${provider}-${label}`;
          }
          return label === el.label;
        }),
      )
    ) {
      setSectionVisible(true);
    } else {
      setSectionVisible(false);
    }
  }, [listElements, gridElements]);

  const renderListContent = () => {
    if (provider === "creator") {
      return (
        <ToolbarListCreatorMetaFields
          format={format}
          campaignId={campaignId}
          toolbarListProps={toolbarListProps}
        />
      );
    }

    if (provider === "publication") {
      return (
        <ToolbarListPublicationMetaFields
          format={format}
          toolbarListProps={toolbarListProps}
          campaignId={campaignId}
        />
      );
    }

    if (provider.includes("publication-summary-")) {
      return (
        <ToolbarListPublicationSummaryByProvider
          campaignId={campaignId}
          provider={provider.replace("publication-summary-", "")}
        />
      );
    }

    if (provider === "table-summary") {
      return (
        <ToolbarListTableSummary
          campaignId={campaignId}
          format={format}
          toolbarListProps={toolbarListProps}
        />
      );
    }

    if (provider.includes("table-summary-")) {
      return (
        <ToolbarListTableSummaryByProvider
          campaignId={campaignId}
          provider={socialProvider}
        />
      );
    }

    return listElements
      ?.sort((a, b) => {
        if (a.value.includes("overlap")) return 0;
        return a.value.localeCompare(b.value);
      })
      ?.map((item) => {
        return (
          <ToolbarListItem
            key={item.value}
            item={item}
            provider={provider}
            format={format}
            toolbarListProps={toolbarListProps}
            isReport={isReport}
          />
        );
      });
  };

  const hideTooltip =
    listElements?.length ||
    dynamicProvider ||
    provider.includes("table-summary");

  return (
    <div
      className={classNames("toolbar-list", {
        "toolbar-list--closed": !showList,
        "toolbar-list--dynamic-provider": dynamicProvider,
        "toolbar-list--no-bottom-border": noBottomBorder,
      })}
    >
      <div className="toolbar-list__content">
        <div className="toolbar-list__item">
          <div className="toolbar-list__item-wrapper">
            {!hideCheckbox ? (
              <MaterialTooltip
                id={`cannot-add-${provider}-tooltip`}
                center
                content={
                  <IDHFormattedMessage
                    id={`ws_report_${provider}_cannot_be_added`}
                    defaultMessage={`Section ${renderHeader()} cannot be added.`}
                  />
                }
                contentHidden={hideTooltip}
                clickable={false}
                className="coupon-tooltip"
                contentClassName=""
              >
                <CustomCheckbox
                  id={`${dynamicProvider || provider}-section`}
                  checked={sectionVisible}
                  onChange={toggleSectionVisibility}
                  blue={!dynamicProvider}
                  disabled={!hideTooltip}
                />
              </MaterialTooltip>
            ) : (
              <div className="toolbar-list__checkbox-placeholder" />
            )}

            {isReport ? (
              <a href={getSectionId()}>{renderHeader()}</a>
            ) : (
              renderHeader()
            )}
          </div>
          {!disableExpanding && listElements.length ? (
            <span
              className={classNames("toolbar-list__item-button", {
                "toolbar-list__item-button--clicked": showList,
              })}
              onClick={() => setShowList((v) => !v)}
            >
              <ChevronDown />
            </span>
          ) : null}
        </div>

        {showList && !disableExpanding && (
          <div className="toolbar-list__list" key={listElements.length}>
            {renderListContent()}
          </div>
        )}
      </div>
    </div>
  );
}

export default injectIntl(ToolbarList);
